import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Button, Grid, Header, Message, MessageHeader, Segment } from "semantic-ui-react";
import moment from "moment";

import { rasterDataLayerUploadsQueryOptions, deleteRasterDataLayerUpload } from "../../../../apis/dataLayerApi";
import SimpleOkCancelModal from "../../../Lumber/SimpleOkCancelModal";
import { useFieldAssociationConfirmation } from "../../../../hooks/useFieldAssociationConfirmation";
import { useAuth0 } from "../../../../auth/auth0";
import { useUserAuth } from "../../../../hooks/useUserAuth";
import RasterUploadRow from "./RasterUploadRow";
import type {
	DataLayerOptions,
	DataLayerTypeSelectOption,
	DataLayerUpload,
	UUID,
} from "../../../../apis/types/dataLayerTypes";
import { AerialPlotResponse } from "../../../../apis/types";
import { FIELD_ASSOC_RESET_HEADER, FIELD_ASSOC_RESET_BODY } from "./constants";
import styles from "./styles.module.css";

export interface RasterDataContentProps {
	dataLayerId: string;
	dataLayerOptions: DataLayerOptions | undefined;
	dataLayerHasExistingFieldAssociations: boolean;
	onResetExistingFieldAssociations: () => void;
	onLoadingChange: (loading: boolean) => void;
}

const RasterDataContent = ({
	dataLayerId,
	dataLayerOptions,
	dataLayerHasExistingFieldAssociations,
	onResetExistingFieldAssociations,
	onLoadingChange,
}: RasterDataContentProps) => {
	const { getTokenSilently } = useAuth0() as { getTokenSilently: () => Promise<string> };
	const userAuth = useUserAuth() as { currentClientId: string };
	const queryClient = useQueryClient();

	/*
	 * State
	 */

	const [isDeletingUUID, setIsDeletingUUID] = useState<UUID | undefined>(undefined);
	const [isDeletingAll, setIsDeletingAll] = useState(false);
	const [failedToLoad, setFailedToLoad] = useState(false);
	const [pendingUploads, setPendingUploads] = useState<DataLayerUpload[]>([]);
	const [deleteAllModalIsOpen, setDeleteAllModalIsOpen] = useState(false);

	/*
	 * Queries and Mutations
	 */

	const {
		data: rasterUploads,
		error: rasterUploadsError,
		refetch: rasterUploadsRefetch,
		isPending: rasterUploadsIsPending,
	} = useQuery(rasterDataLayerUploadsQueryOptions(getTokenSilently, userAuth.currentClientId, dataLayerId));

	const { mutateAsync: deleteRasterDataLayerUploadMutationAsync } = useMutation({
		mutationFn: deleteRasterDataLayerUpload,
		onSuccess: () => {
			void rasterUploadsRefetch();
		},
		onSettled: () => {
			setIsDeletingUUID(undefined);
		},
	});

	/*
	 * Derived State
	 */

	const anyQueryLoading = useMemo(() => rasterUploadsIsPending, [rasterUploadsIsPending]);

	// Combine server data with pending items for display
	const allUploads = useMemo(() => [...(rasterUploads || []), ...pendingUploads], [rasterUploads, pendingUploads]);

	/*
	 * Effects
	 */

	// Generate at least one pending upload record locally if there are no uploads yet.
	useEffect(() => {
		if (rasterUploads !== undefined && rasterUploads.length === 0) {
			setPendingUploads((prev) => {
				if (prev.length > 0) {
					return prev;
				}
				return [
					{
						id: `pending-${crypto.randomUUID()}`,
						dataLayerId,
						dataLayerTypeId: "",
						rawDataUrlPath: "",
						createdDateTimeUtc: moment.utc().toDate(),
					},
				];
			});
		}
	}, [rasterUploads, dataLayerId]);

	// Handle rasterUploads query failure
	useEffect(() => {
		if (rasterUploadsError !== null) {
			console.error(rasterUploadsError);
			toast.error(rasterUploadsError.message);
			setFailedToLoad(true);
		}
	}, [rasterUploadsError]);

	useEffect(() => {
		onLoadingChange(anyQueryLoading);
	}, [anyQueryLoading, onLoadingChange]);

	/*
	 * Callbacks
	 */

	const handleAdd = useCallback(() => {
		const newRasterUpload: DataLayerUpload = {
			id: `pending-${crypto.randomUUID()}`,
			dataLayerId,
			dataLayerTypeId: "",
			rawDataUrlPath: "",
			createdDateTimeUtc: moment.utc().toDate(),
		};
		setPendingUploads((prev) => [...prev, newRasterUpload]);
	}, [dataLayerId]);

	const handleDelete = useCallback(
		async (upload: DataLayerUpload) => {
			setIsDeletingUUID(upload.id);
			if (rasterUploads?.some((r) => r.id === upload.id)) {
				const toastId = `raster-data-upload-delete-${upload.id}`;
				toast.loading(`Deleting raster data layer upload...`, { toastId, type: "info" });
				const clientId = userAuth.currentClientId;
				try {
					await deleteRasterDataLayerUploadMutationAsync({
						getAccessToken: getTokenSilently,
						clientId,
						dataLayerUploadId: upload.id,
					});
					toast.dismiss(toastId);
				} catch (error) {
					console.error(error);
					toast.dismiss(toastId);
					toast.error(error instanceof Error ? error.message : String(error));
				}
			} else if (pendingUploads.some((p) => p.id === upload.id)) {
				setPendingUploads((prev) => prev.filter((p) => p.id !== upload.id));
			}
			setIsDeletingUUID(undefined);
		},
		[
			userAuth.currentClientId,
			rasterUploads,
			pendingUploads,
			getTokenSilently,
			deleteRasterDataLayerUploadMutationAsync,
		],
	);

	const handleDeleteAllModalOk = useCallback(async () => {
		setDeleteAllModalIsOpen(false);
		setPendingUploads([]);
		setIsDeletingAll(true);
		const toastId = "raster-data-upload-delete-all";
		try {
			if (rasterUploads) {
				const clientId = userAuth.currentClientId;
				// Optimistically clear the list before mutations run
				queryClient.setQueryData(
					rasterDataLayerUploadsQueryOptions(getTokenSilently, clientId, dataLayerId).queryKey,
					[],
				);
				toast.loading(`Deleting raster data layer upload 1 of ${rasterUploads.length}...`, {
					toastId,
					type: "info",
					autoClose: false,
				});
				for (const [index, upload] of rasterUploads.entries()) {
					toast.update(toastId, {
						render: `Deleting raster data layer upload ${index + 1}/${rasterUploads.length}...`,
						type: "info",
						isLoading: true,
						autoClose: false,
					});
					await deleteRasterDataLayerUpload({
						getAccessToken: getTokenSilently,
						clientId,
						dataLayerUploadId: upload.id,
					});
				}
				void rasterUploadsRefetch();
				toast.dismiss(toastId);
			}
		} catch (error) {
			console.error(error);
			toast.dismiss(toastId);
			toast.error(error instanceof Error ? error.message : String(error));
		} finally {
			setIsDeletingAll(false);
		}
	}, [userAuth.currentClientId, rasterUploads, getTokenSilently, rasterUploadsRefetch, dataLayerId, queryClient]);

	const handleDeleteAllModalRequest = useCallback(() => {
		setDeleteAllModalIsOpen(true);
	}, []);

	const handleDeleteAllModalCancel = useCallback(() => {
		setDeleteAllModalIsOpen(false);
	}, []);

	const {
		fieldAssocModalIsOpen,
		handleFieldAssocModalOk,
		handleFieldAssocModalCancel,
		withFieldAssociationConfirmation,
	} = useFieldAssociationConfirmation(dataLayerHasExistingFieldAssociations, onResetExistingFieldAssociations);

	/**
	 * Handle upload (POST) completion callback from RasterDataLayerCtrl component.
	 * Optimistically write the new upload into the cache before removing the pending item
	 * so allUploads never loses the row during the async refetch (prevents flash of empty content).
	 */
	const handleUploadResponse = useCallback(
		(pendingId: UUID, _newId: UUID, response: AerialPlotResponse<DataLayerUpload>) => {
			const { queryKey } = rasterDataLayerUploadsQueryOptions(getTokenSilently, userAuth.currentClientId, dataLayerId);
			queryClient.setQueryData(queryKey, (old: DataLayerUpload[] | undefined) => [...(old || []), response.data]);
			setPendingUploads((prev) => prev.filter((upload) => upload.id !== pendingId));
			void rasterUploadsRefetch();
		},
		[queryClient, getTokenSilently, userAuth.currentClientId, dataLayerId, rasterUploadsRefetch],
	);

	/*
	 * Gated action wrappers — if the data layer has existing field associations,
	 * show a confirmation modal before proceeding with the destructive action.
	 */

	const gatedHandleDelete = useCallback(
		(upload: DataLayerUpload) => {
			withFieldAssociationConfirmation(() => void handleDelete(upload));
		},
		[withFieldAssociationConfirmation, handleDelete],
	);

	const gatedHandleDeleteAllModalRequest = useCallback(() => {
		withFieldAssociationConfirmation(() => handleDeleteAllModalRequest());
	}, [withFieldAssociationConfirmation, handleDeleteAllModalRequest]);

	// Build select options filtered to the "Raster Data" group by name
	const dataLayerTypeSelectOptions = useMemo<DataLayerTypeSelectOption[]>(() => {
		if (!dataLayerOptions) {
			return [];
		}
		const rasterGroup = dataLayerOptions.dataLayerGroups.find((g) => g.name === "Raster Data");
		if (!rasterGroup) {
			return [];
		}
		return dataLayerOptions.dataLayerTypes
			.filter((dlt) => dlt.dataLayerGroupId === rasterGroup.id)
			.map((dlt) => ({
				key: dlt.id,
				value: dlt.id,
				text: dlt.presetName || dlt.name,
			}));
	}, [dataLayerOptions]);

	/*
	 * Rendering
	 */

	if (anyQueryLoading) {
		return null;
	}

	if (failedToLoad) {
		return (
			<Segment basic>
				<Message negative>
					<MessageHeader>Error</MessageHeader>
					<p>Error while preparing Source Raster Uploader. Please try again.</p>
				</Message>
			</Segment>
		);
	}

	return (
		<>
			<Header as="h2">Continuous Factors (Raster Data)</Header>
			{allUploads.length > 0 && (
				<div className={styles.uploadRowsList}>
					{allUploads.map((rasterUpload) => (
						<Segment key={rasterUpload.id} vertical basic className={`${styles.segment} ${styles.uploadRowSegment}`}>
							<RasterUploadRow
								clientId={userAuth.currentClientId}
								dataLayerId={dataLayerId}
								dataLayerTypeSelectOptions={dataLayerTypeSelectOptions}
								upload={rasterUpload}
								onBeforeModify={withFieldAssociationConfirmation}
								onUploadResponse={handleUploadResponse}
								onDelete={gatedHandleDelete}
								isDeleting={isDeletingAll || rasterUpload.id === isDeletingUUID}
								isDisabled={false}
							/>
						</Segment>
					))}
				</div>
			)}
			<Segment vertical basic className={`${styles.segment} ${styles.uploadFooterActions}`}>
				<Grid stackable className={styles.uploadFooterRow}>
					<Grid.Row verticalAlign="middle">
						<Grid.Column computer={6} className={styles.uploadFooterLeftCol}>
							<Button content="Add Source Raster" icon="add" compact onClick={handleAdd} />
						</Grid.Column>
						<Grid.Column computer={10} textAlign="right" className={styles.uploadFooterRightCol}>
							{rasterUploads && rasterUploads.length > 0 && (
								<SimpleOkCancelModal
									headerMsg="Confirm Delete all"
									bodyMsg="All source raster data uploads associated with this data layer will be deleted permanently."
									open={deleteAllModalIsOpen}
									onOpen={gatedHandleDeleteAllModalRequest}
									onOk={() => void handleDeleteAllModalOk()}
									onCancel={handleDeleteAllModalCancel}
									trigger={<Button content="Delete All Above Uploads" icon="trash" compact />}
								/>
							)}
						</Grid.Column>
					</Grid.Row>
				</Grid>
			</Segment>

			<SimpleOkCancelModal
				headerMsg={FIELD_ASSOC_RESET_HEADER}
				bodyMsg={FIELD_ASSOC_RESET_BODY}
				open={fieldAssocModalIsOpen}
				onOk={handleFieldAssocModalOk}
				onCancel={handleFieldAssocModalCancel}
			/>
		</>
	);
};

export default RasterDataContent;
